import React, { useState, useMemo } from 'react';
import { Layout } from './Layout';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DateRangeFilter, DateRange } from './DateRangeFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Avatar, AvatarFallback } from './ui/avatar';
import { 
  Home, 
  Shield, 
  UserX, 
  Search, 
  Filter, 
  Eye, 
  Download, 
  RefreshCw,
  User,
  Clock,
  MapPin,
  Monitor,
  Smartphone,
  Globe,
  LogIn,
  LogOut,
  MousePointer,
  FileText,
  Settings,
  AlertTriangle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { toast } from 'sonner';

// Mock user activity data
const generateUserActivityData = () => {
  const users = [
    { name: 'John Smith', email: 'john.smith@hospital.com', role: 'Auditor', department: 'Quality Assurance' },
    { name: 'Sarah Johnson', email: 'sarah.johnson@hospital.com', role: 'Supervisor', department: 'Quality Assurance' },
    { name: 'Mike Chen', email: 'mike.chen@hospital.com', role: 'Clinician', department: 'Cardiology' },
    { name: 'Lisa Davis', email: 'lisa.davis@hospital.com', role: 'Admin', department: 'IT' },
    { name: 'Dr. Brown', email: 'dr.brown@hospital.com', role: 'Physician', department: 'Internal Medicine' },
    { name: 'Admin User', email: 'admin@hospital.com', role: 'System Admin', department: 'IT' },
  ];

  const actions = [
    'Login successful',
    'Logout',
    'Audit completed',
    'Report generated',
    'Settings modified',
    'User profile updated',
    'Password changed',
    'Data exported',
    'File uploaded',
    'Permission granted',
    'Permission revoked',
    'Session timeout',
    'Failed login attempt',
    'Configuration updated',
    'Backup initiated',
    'Database query executed',
    'Email sent',
    'Document accessed',
    'Record created',
    'Record updated'
  ];

  const devices = ['Desktop - Windows', 'Desktop - Mac', 'Mobile - iOS', 'Mobile - Android', 'Tablet - iPad'];
  const browsers = ['Chrome 118', 'Firefox 119', 'Safari 17', 'Edge 118'];
  const locations = ['New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Remote'];

  return Array.from({ length: 1000 }, (_, i) => {
    const user = users[Math.floor(Math.random() * users.length)]!;
    const action = actions[Math.floor(Math.random() * actions.length)]!;
    const device = devices[Math.floor(Math.random() * devices.length)]!;
    const browser = browsers[Math.floor(Math.random() * browsers.length)]!;
    const location = locations[Math.floor(Math.random() * locations.length)]!;
    const timestamp = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000);
    const isSuccessful = !action.includes('Failed') && Math.random() > 0.1;
    
    return {
      id: `user-${i + 1}`,
      timestamp,
      user: user.name,
      email: user.email,
      role: user.role,
      department: user.department,
      action,
      isSuccessful,
      device,
      browser,
      location,
      ipAddress: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
      sessionDuration: Math.floor(Math.random() * 480) + 30, // 30-510 minutes
      resourcesAccessed: Math.floor(Math.random() * 20) + 1,
      dataTransferred: Math.floor(Math.random() * 100) + 1, // MB
      riskScore: Math.floor(Math.random() * 100),
      details: generateActionDetails(action, user.name),
    };
  }).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

const generateActionDetails = (action: string, userName: string) => {
  const details = {
    'Login successful': `${userName} successfully authenticated using multi-factor authentication. Session established with 8-hour expiry.`,
    'Audit completed': `${userName} completed audit review for patient case. 12 goals reviewed with 91% confidence average.`,
    'Report generated': `${userName} generated quarterly compliance report. Report includes 450 audits from last 3 months.`,
    'Settings modified': `${userName} updated notification preferences and dashboard layout. Changes saved to user profile.`,
    'Failed login attempt': `Failed authentication attempt for ${userName}. Incorrect password provided. Account not locked.`,
    'Data exported': `${userName} exported audit data to CSV format. 150 records included with patient data redacted.`,
  };
  
  return details[action as keyof typeof details] || `${userName} performed ${action.toLowerCase()} with standard system permissions.`;
};

export function UserActivityTracker() {
  const [userData] = useState(generateUserActivityData());
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(Date.now() - 24 * 60 * 60 * 1000), // Last 24 hours
    to: new Date(),
  });
  const [filters, setFilters] = useState({
    user: 'all',
    role: 'all',
    action: 'all',
    status: 'all',
    searchQuery: '',
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter data based on current filters
  const filteredData = useMemo(() => {
    return userData.filter(activity => {
      // Date range filter
      if (dateRange.from && dateRange.to) {
        if (activity.timestamp < dateRange.from || activity.timestamp > dateRange.to) {
          return false;
        }
      }

      // User filter
      if (filters.user !== 'all' && activity.user !== filters.user) {
        return false;
      }

      // Role filter
      if (filters.role !== 'all' && activity.role !== filters.role) {
        return false;
      }

      // Action filter
      if (filters.action !== 'all' && !activity.action?.toLowerCase().includes(filters.action.toLowerCase())) {
        return false;
      }

      // Status filter
      if (filters.status !== 'all') {
        if (filters.status === 'successful' && !activity.isSuccessful) return false;
        if (filters.status === 'failed' && activity.isSuccessful) return false;
      }

      // Search query
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        return (
          activity.user?.toLowerCase().includes(query) ||
          activity.action?.toLowerCase().includes(query) ||
          activity.role?.toLowerCase().includes(query) ||
          activity.department?.toLowerCase().includes(query) ||
          activity.ipAddress?.includes(query)
        );
      }

      return true;
    });
  }, [userData, dateRange, filters]);

  const handleViewDetails = (activity: any) => {
    setSelectedActivity(activity);
    setShowDetails(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
      toast.success('User activity data refreshed');
    }, 1000);
  };

  const handleExport = () => {
    toast.success('User activity log exported to CSV');
  };

  const getUniqueUsers = () => {
    return Array.from(new Set(userData.map(activity => activity.user))).sort();
  };

  const getUniqueRoles = () => {
    return Array.from(new Set(userData.map(activity => activity.role))).sort();
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const total = filteredData.length;
    const failed = filteredData.filter(a => !a.isSuccessful).length;
    const uniqueUsers = new Set(filteredData.map(a => a.user)).size;
    const highRisk = filteredData.filter(a => a.riskScore > 70).length;
    const avgSessionDuration = total > 0 ? (filteredData.reduce((sum, a) => sum + a.sessionDuration, 0) / total).toFixed(0) : '0';
    
    return { total, failed, uniqueUsers, highRisk, avgSessionDuration };
  }, [filteredData]);

  // Prepare activity timeline data
  const timelineData = useMemo(() => {
    const hourlyData = new Map();
    
    filteredData.forEach(activity => {
      const hour = new Date(activity.timestamp).getHours();
      const key = `${hour.toString().padStart(2, '0')}:00`;
      if (!hourlyData.has(key)) {
        hourlyData.set(key, { time: key, activities: 0, failed: 0 });
      }
      const data = hourlyData.get(key);
      data.activities++;
      if (!activity.isSuccessful) data.failed++;
    });
    
    return Array.from(hourlyData.values()).sort((a, b) => a.time.localeCompare(b.time));
  }, [filteredData]);

  const getRiskLevelBadge = (score: number) => {
    if (score > 70) return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">High Risk</Badge>;
    if (score > 40) return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">Medium Risk</Badge>;
    return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Low Risk</Badge>;
  };

  return (
    <Layout>
      <div className="h-full flex flex-col" style={{ fontSize: '11.2px' }}>
        {/* Breadcrumb Navigation */}
        <div className="p-8 pb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2" style={{ fontSize: '11.2px' }}>
                  <Home className="w-3 h-3" />
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2" style={{ fontSize: '11.2px' }}>
                  <Shield className="w-3 h-3" />
                  Admin Audit
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage style={{ fontSize: '11.2px' }}>User Activity</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Header */}
        <div className="px-8 pb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-gray-900 mb-2" style={{ fontSize: '19.6px', fontWeight: 600 }}>
                User Activity Tracker
              </h1>
              <p className="text-gray-600" style={{ fontSize: '11.2px' }}>
                Comprehensive monitoring of user actions and behavior patterns
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" onClick={handleExport}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>

          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <MousePointer className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.total}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Total Activities
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <User className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.uniqueUsers}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Active Users
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.failed}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Failed Actions
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Shield className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.highRisk}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      High Risk Events
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.avgSessionDuration}m
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Avg Session
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Activity Timeline Chart */}
        <div className="px-8 pb-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: '14px' }}>User Activity Timeline</CardTitle>
              <CardDescription style={{ fontSize: '11.2px' }}>
                Hourly distribution of user activities and failures
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div style={{ height: '250px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={timelineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="time" stroke="#6B7280" fontSize={9.6} />
                    <YAxis stroke="#6B7280" fontSize={9.6} />
                    <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                    <Bar dataKey="activities" fill="#3B82F6" name="Total Activities" />
                    <Bar dataKey="failed" fill="#EF4444" name="Failed Activities" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="px-8 pb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontSize: '14px' }}>
                <Filter className="w-4 h-4" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                {/* Date Range */}
                <div className="md:col-span-2">
                  <DateRangeFilter
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                  />
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search activities..."
                    value={filters.searchQuery}
                    onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                    className="pl-9"
                    style={{ fontSize: '11.2px' }}
                  />
                </div>

                {/* User Filter */}
                <Select value={filters.user} onValueChange={(value) => setFilters(prev => ({ ...prev, user: value }))}>
                  <SelectTrigger style={{ fontSize: '11.2px' }}>
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {getUniqueUsers().map((user) => (
                      <SelectItem key={user} value={user}>
                        {user}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Role Filter */}
                <Select value={filters.role} onValueChange={(value) => setFilters(prev => ({ ...prev, role: value }))}>
                  <SelectTrigger style={{ fontSize: '11.2px' }}>
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {getUniqueRoles().map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Status Filter */}
                <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger style={{ fontSize: '11.2px' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="successful">Successful</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Activity Table */}
        <div className="flex-1 px-8 pb-8">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: '14px' }}>User Activities ({filteredData.length} entries)</CardTitle>
              <CardDescription style={{ fontSize: '11.2px' }}>
                Detailed log of all user actions and system interactions
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead style={{ fontSize: '11.2px' }}>User</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Action</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Timestamp</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Device</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Location</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Risk Level</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Status</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.slice(0, 50).map((activity) => (
                      <TableRow key={activity.id} className={!activity.isSuccessful ? 'bg-red-50' : activity.riskScore > 70 ? 'bg-yellow-50' : ''}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                                {activity.user?.split(' ').map(n => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p style={{ fontSize: '11.2px', fontWeight: 500 }}>
                                {activity.user}
                              </p>
                              <p className="text-gray-500" style={{ fontSize: '9.6px' }}>
                                {activity.role}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p style={{ fontSize: '11.2px', fontWeight: 500 }}>
                              {activity.action}
                            </p>
                            <p className="text-gray-500" style={{ fontSize: '9.6px' }}>
                              {activity.department}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell style={{ fontSize: '11.2px' }}>
                          <div>
                            <p>{activity.timestamp.toLocaleDateString()}</p>
                            <p className="text-gray-500" style={{ fontSize: '9.6px' }}>
                              {activity.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {activity.device?.includes('Mobile') ? (
                              <Smartphone className="w-4 h-4 text-gray-400" />
                            ) : (
                              <Monitor className="w-4 h-4 text-gray-400" />
                            )}
                            <div>
                              <p style={{ fontSize: '11.2px' }}>{activity.device}</p>
                              <p className="text-gray-500" style={{ fontSize: '9.6px' }}>
                                {activity.browser}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <div>
                              <p style={{ fontSize: '11.2px' }}>{activity.location}</p>
                              <p className="text-gray-500" style={{ fontSize: '9.6px' }}>
                                {activity.ipAddress}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getRiskLevelBadge(activity.riskScore)}
                        </TableCell>
                        <TableCell>
                          {activity.isSuccessful ? (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                              Success
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
                              Failed
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" onClick={() => handleViewDetails(activity)}>
                            <Eye className="w-3 h-3 mr-1" />
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity Details Dialog */}
        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>User Activity Details</DialogTitle>
              <DialogDescription>
                Comprehensive information about the selected user activity
              </DialogDescription>
            </DialogHeader>
            
            {selectedActivity && (
              <div className="space-y-6">
                {/* User Information */}
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <Avatar className="w-12 h-12">
                    <AvatarFallback className="bg-blue-100 text-blue-700">
                      {selectedActivity.user?.split(' ').map((n: string) => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <h3 className="text-gray-900" style={{ fontSize: '14px', fontWeight: 600 }}>
                      {selectedActivity.user}
                    </h3>
                    <p className="text-gray-600" style={{ fontSize: '11.2px' }}>
                      {selectedActivity.role} • {selectedActivity.department}
                    </p>
                    <p className="text-gray-500" style={{ fontSize: '10px' }}>
                      {selectedActivity.email}
                    </p>
                  </div>
                  {getRiskLevelBadge(selectedActivity.riskScore)}
                </div>

                {/* Activity Information */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Action</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedActivity.action}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Timestamp</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedActivity.timestamp.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Session Duration</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedActivity.sessionDuration} minutes
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Data Transferred</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedActivity.dataTransferred} MB
                    </p>
                  </div>
                </div>

                {/* Device and Location */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="text-blue-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                      Device Information
                    </h4>
                    <div className="space-y-2">
                      <p className="text-blue-800" style={{ fontSize: '11.2px' }}>
                        <strong>Device:</strong> {selectedActivity.device}
                      </p>
                      <p className="text-blue-800" style={{ fontSize: '11.2px' }}>
                        <strong>Browser:</strong> {selectedActivity.browser}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="text-green-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                      Location Information
                    </h4>
                    <div className="space-y-2">
                      <p className="text-green-800" style={{ fontSize: '11.2px' }}>
                        <strong>Location:</strong> {selectedActivity.location}
                      </p>
                      <p className="text-green-800" style={{ fontSize: '11.2px' }}>
                        <strong>IP Address:</strong> {selectedActivity.ipAddress}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Activity Details */}
                <div>
                  <h4 className="text-gray-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                    Activity Details
                  </h4>
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <p style={{ fontSize: '11.2px' }}>{selectedActivity.details}</p>
                  </div>
                </div>

                {/* Risk Assessment */}
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="text-yellow-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                    Risk Assessment
                  </h4>
                  <div className="flex items-center gap-3">
                    <div className="text-center">
                      <p className="text-yellow-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                        {selectedActivity.riskScore}
                      </p>
                      <p className="text-yellow-700" style={{ fontSize: '9.6px' }}>Risk Score</p>
                    </div>
                    <div className="text-center">
                      <p className="text-yellow-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                        {selectedActivity.resourcesAccessed}
                      </p>
                      <p className="text-yellow-700" style={{ fontSize: '9.6px' }}>Resources Accessed</p>
                    </div>
                  </div>
                  <p className="text-yellow-800 mt-2" style={{ fontSize: '10px' }}>
                    Risk score calculated based on user behavior patterns, access patterns, and historical data.
                  </p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}